import { useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { X, PaperPlaneTilt, Calendar, Trash, ArrowsClockwise, Plus } from "@phosphor-icons/react";
import { api } from "@/api";
import { Button } from "@/components/ui/button";

export function AlertsControlPanel({ open, onClose }) {
  const qc = useQueryClient();
  const [chatIdInput, setChatIdInput] = useState("");
  const [minScoreInput, setMinScoreInput] = useState("");
  const [evLabel, setEvLabel] = useState("");
  const [evTime, setEvTime] = useState("");
  const [evWindow, setEvWindow] = useState(60);

  const tg = useQuery({
    queryKey: ["telegram-status"],
    queryFn: api.telegramStatus,
    enabled: open,
    refetchInterval: open ? 15_000 : false,
  });
  const news = useQuery({
    queryKey: ["news-upcoming"],
    queryFn: () => api.newsUpcoming(168),
    enabled: open,
    refetchInterval: open ? 30_000 : false,
  });

  const discover = useMutation({
    mutationFn: api.telegramDiscover,
    onSuccess: (d) => {
      toast.success(
        d.new_subscribers?.length
          ? `Registered ${d.new_subscribers.length} new chat(s)`
          : `Found ${d.discovered?.length || 0} chats — none new. Send /start to your bot then retry.`
      );
      qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
    onError: () => toast.error("Discover failed"),
  });
  const register = useMutation({
    mutationFn: (cid) => api.telegramRegister(cid),
    onSuccess: (d) => {
      toast.success(d.test_message_sent ? "Registered + test message sent" : "Registered (test send failed — bot may need /start)");
      setChatIdInput("");
      qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
    onError: () => toast.error("Register failed"),
  });
  const remove = useMutation({
    mutationFn: (cid) => api.telegramConfig({ remove_chat_id: cid }),
    onSuccess: () => {
      toast.success("Removed subscriber");
      qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
  const updateScore = useMutation({
    mutationFn: (s) => api.telegramConfig({ min_score: s }),
    onSuccess: () => {
      toast.success("Threshold updated");
      qc.invalidateQueries({ queryKey: ["telegram-status"] });
    },
  });
  const addEvent = useMutation({
    mutationFn: (payload) => api.addNewsEvent(payload),
    onSuccess: () => {
      toast.success("Event added");
      setEvLabel(""); setEvTime("");
      qc.invalidateQueries({ queryKey: ["news-upcoming"] });
    },
    onError: () => toast.error("Add failed"),
  });
  const delEvent = useMutation({
    mutationFn: (id) => api.deleteNewsEvent(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ["news-upcoming"] }),
  });

  if (!open) return null;

  const submitEvent = () => {
    if (!evLabel || !evTime) {
      toast.error("Label + time required");
      return;
    }
    const iso = new Date(evTime).toISOString();
    addEvent.mutate({ kind: "CUSTOM", label: evLabel, time: iso, window_minutes: Number(evWindow) || 60, impact: "medium" });
  };

  return (
    <div
      data-testid="alerts-panel"
      className="fixed inset-0 z-50 bg-black/85 backdrop-blur-sm flex overflow-y-auto"
      onClick={onClose}
    >
      <div
        className="w-full max-w-3xl mx-auto my-8 bg-[#0A0A0A] border border-white/10 h-fit"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="sticky top-0 z-10 bg-[#0f0f0f] border-b border-white/10 px-6 py-3 flex items-center gap-3">
          <PaperPlaneTilt size={18} className="text-[#00FF66]" />
          <h2 className="font-display text-lg font-bold tracking-widest uppercase text-white">
            Alerts &amp; News Blackout
          </h2>
          <Button
            data-testid="close-alerts-panel"
            onClick={onClose}
            variant="outline"
            size="sm"
            className="ml-auto rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white"
          >
            <X size={14} />
          </Button>
        </div>

        <div className="p-6 space-y-6">
          {/* TELEGRAM */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <PaperPlaneTilt size={14} className="text-[#00FF66]" />
              <h3 className="font-display text-sm font-bold tracking-widest uppercase text-white">
                Telegram Push
              </h3>
              <span className={`ml-2 font-mono text-[10px] px-2 py-0.5 border ${tg.data?.enabled ? "border-[#00FF66]/60 text-[#00FF66]" : "border-white/10 text-white/50"}`}>
                {tg.data?.enabled ? "BOT ONLINE" : "TOKEN MISSING"}
              </span>
              <span className="ml-auto font-mono text-[11px] text-white/50">
                {tg.data?.sent_count ?? 0} messages sent
              </span>
            </div>

            <div className="text-[12px] text-white/60 mb-3 leading-relaxed">
              Message your bot with <code className="text-[#F5C518] px-1">/start</code> in Telegram, then hit
              <b className="text-white"> Discover </b> to auto-register your chat ID. Or paste a chat ID manually.
            </div>

            <div className="flex flex-wrap items-center gap-2 mb-3">
              <Button
                data-testid="tg-discover-btn"
                onClick={() => discover.mutate()}
                disabled={discover.isPending || !tg.data?.enabled}
                variant="outline"
                size="sm"
                className="rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white font-mono text-[11px] uppercase tracking-widest"
              >
                <ArrowsClockwise size={12} className={discover.isPending ? "animate-spin mr-1.5" : "mr-1.5"} />
                Discover subscribers
              </Button>
              <input
                data-testid="tg-chatid-input"
                value={chatIdInput}
                onChange={(e) => setChatIdInput(e.target.value)}
                placeholder="or paste chat_id"
                className="bg-black border border-white/10 px-2 py-1 text-white font-mono text-[12px] w-40"
              />
              <Button
                data-testid="tg-register-btn"
                onClick={() => chatIdInput && register.mutate(Number(chatIdInput))}
                disabled={register.isPending || !chatIdInput}
                variant="outline"
                size="sm"
                className="rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white font-mono text-[11px] uppercase tracking-widest"
              >
                <Plus size={12} className="mr-1" /> Register
              </Button>
              <div className="ml-auto flex items-center gap-2">
                <span className="font-mono text-[11px] text-white/50">Min Score</span>
                <input
                  data-testid="tg-min-score"
                  type="number"
                  min={50}
                  max={100}
                  value={minScoreInput || tg.data?.min_score || 72}
                  onChange={(e) => setMinScoreInput(e.target.value)}
                  className="bg-black border border-white/10 px-2 py-1 text-white font-mono text-[12px] w-16"
                />
                <Button
                  onClick={() => minScoreInput && updateScore.mutate(Number(minScoreInput))}
                  disabled={!minScoreInput}
                  variant="outline"
                  size="sm"
                  className="rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white font-mono text-[11px] uppercase tracking-widest"
                >
                  Save
                </Button>
              </div>
            </div>

            <div className="border border-white/10">
              <div className="px-3 py-1.5 border-b border-white/10 bg-[#0f0f0f] font-mono text-[10px] uppercase tracking-widest text-white/50 flex">
                <span>Chat ID</span>
                <span className="ml-auto">Actions</span>
              </div>
              {(tg.data?.subscribers || []).length === 0 && (
                <div className="p-4 text-center text-white/40 font-mono text-[11px]">
                  No subscribers yet — send /start to your bot, then click Discover.
                </div>
              )}
              {(tg.data?.subscribers || []).map((c) => (
                <div key={c} className="px-3 py-1.5 border-b border-white/5 flex items-center gap-2 font-mono text-[12px]">
                  <span className="text-white">{c}</span>
                  <button
                    data-testid={`tg-remove-${c}`}
                    onClick={() => remove.mutate(c)}
                    className="ml-auto text-white/50 hover:text-[#FF3B30]"
                    title="Unsubscribe"
                  >
                    <Trash size={12} />
                  </button>
                </div>
              ))}
            </div>
          </section>

          {/* NEWS BLACKOUT */}
          <section>
            <div className="flex items-center gap-2 mb-3">
              <Calendar size={14} className="text-[#F5C518]" />
              <h3 className="font-display text-sm font-bold tracking-widest uppercase text-white">
                News &amp; Event Blackout
              </h3>
              {news.data?.blackout_active && (
                <span className="font-mono text-[10px] px-2 py-0.5 border border-[#FF3B30]/60 text-[#FF3B30] bg-[#FF3B30]/10">
                  BLACKOUT ACTIVE · {news.data?.current_event?.label}
                </span>
              )}
            </div>

            <div className="text-[12px] text-white/60 mb-3 leading-relaxed">
              Scanner auto-pauses within ±window of each event. CPI and FOMC are built-in
              — add token unlocks, exchange maintenance, or any custom event you know will spike volatility.
            </div>

            <div className="flex flex-wrap items-end gap-2 mb-3">
              <div className="flex flex-col">
                <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">Label</span>
                <input
                  data-testid="news-label"
                  value={evLabel}
                  onChange={(e) => setEvLabel(e.target.value)}
                  placeholder="e.g. SOL Unlock"
                  className="bg-black border border-white/10 px-2 py-1 text-white font-mono text-[12px] w-52"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">Time (local)</span>
                <input
                  data-testid="news-time"
                  type="datetime-local"
                  value={evTime}
                  onChange={(e) => setEvTime(e.target.value)}
                  className="bg-black border border-white/10 px-2 py-1 text-white font-mono text-[12px]"
                />
              </div>
              <div className="flex flex-col">
                <span className="font-mono text-[10px] text-white/50 uppercase tracking-widest">Window (min)</span>
                <input
                  data-testid="news-window"
                  type="number"
                  min={5}
                  max={720}
                  value={evWindow}
                  onChange={(e) => setEvWindow(e.target.value)}
                  className="bg-black border border-white/10 px-2 py-1 text-white font-mono text-[12px] w-20"
                />
              </div>
              <Button
                data-testid="news-add-btn"
                onClick={submitEvent}
                disabled={addEvent.isPending}
                variant="outline"
                size="sm"
                className="rounded-none border-white/15 bg-transparent hover:bg-white/5 text-white font-mono text-[11px] uppercase tracking-widest"
              >
                <Plus size={12} className="mr-1" /> Add
              </Button>
            </div>

            <div className="border border-white/10 max-h-72 overflow-auto" data-testid="news-list">
              <div className="px-3 py-1.5 border-b border-white/10 bg-[#0f0f0f] font-mono text-[10px] uppercase tracking-widest text-white/50 flex">
                <span className="w-16">Kind</span>
                <span className="flex-1">Label</span>
                <span className="w-40">Time (UTC)</span>
                <span className="w-16 text-right">±min</span>
                <span className="w-16 text-right">In</span>
                <span className="w-8" />
              </div>
              {(news.data?.events || []).map((ev) => {
                const isBlackout = news.data?.current_event?.id === ev.id;
                const isCustom = ev.source === "user";
                return (
                  <div
                    key={ev.id}
                    className={`px-3 py-1.5 border-b border-white/5 flex items-center gap-2 font-mono text-[11px] ${
                      isBlackout ? "bg-[#FF3B30]/10 text-[#FF3B30]" : "text-white/70"
                    }`}
                  >
                    <span className="w-16 uppercase text-[10px]">{ev.kind}</span>
                    <span className="flex-1">{ev.label}</span>
                    <span className="w-40 text-white/50">
                      {ev.time.replace("T", " ").slice(0, 16)}
                    </span>
                    <span className="w-16 text-right">{ev.window_minutes}</span>
                    <span className="w-16 text-right text-[#F5C518]">
                      {ev.minutes_from_now > 0
                        ? `${Math.floor(ev.minutes_from_now / 60)}h ${ev.minutes_from_now % 60}m`
                        : "now"}
                    </span>
                    <span className="w-8 flex justify-end">
                      {isCustom && (
                        <button
                          onClick={() => delEvent.mutate(ev.id)}
                          className="text-white/40 hover:text-[#FF3B30]"
                          data-testid={`news-remove-${ev.id}`}
                        >
                          <Trash size={11} />
                        </button>
                      )}
                    </span>
                  </div>
                );
              })}
              {(news.data?.events || []).length === 0 && (
                <div className="p-4 text-center text-white/40 font-mono text-[11px]">
                  No upcoming events.
                </div>
              )}
            </div>
          </section>
        </div>
      </div>
    </div>
  );
}
